import React, { useEffect, useRef, useState } from "react";
import "./UZIP.css";
import { usePreloadedImages } from "../../contexts/PreloadedImagesContext";
import { disableScroll, enableScroll } from "../../utils/scroll-disabling/scroll-disabling";

function UZIP() {
  const { uzipFrames } = usePreloadedImages();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameCount = 110;
  const stopFrame = 80;
  const hasAnimated = useRef(false);
  const firstAnimationDone = useRef(false);
  const secondAnimationDone = useRef(false);
  const [textAnimated, setTextAnimated] = useState(false);
  const [secondAnimationStarted, setSecondAnimationStarted] = useState(false);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    canvas.width = 1158;
    canvas.height = 770;

    const drawFrame = (index: number) => {
      const frame = uzipFrames[index];
      if (!frame) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(frame, 0, 0, canvas.width, canvas.height);
    };

    const animate = (
      startTime: number,
      from: number,
      to: number,
      onComplete?: () => void
    ) => {
      const duration = 2000;
      const now = performance.now();
      const elapsed = now - startTime;

      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const frame = Math.round(easeOut * (to - from)) + from;
      drawFrame(frame);

      if (frame < to) {
        requestAnimationFrame(() => animate(startTime, from, to, onComplete));
      } else if (onComplete) {
        onComplete();
      }
    };

    const drawFirstFrame = () => {
      const checkLoaded = () => {
        if (uzipFrames[1]?.complete) {
          drawFrame(1);
        } else {
          setTimeout(checkLoaded, 50);
        }
      };
      checkLoaded();
    };

    drawFirstFrame();

    const handleScroll = () => {
      const y = window.scrollY;

      if (!hasAnimated.current && y > 20) {
        hasAnimated.current = true;
        setTextAnimated(true);
        disableScroll();
        requestAnimationFrame(() =>
          animate(performance.now(), 1, stopFrame, () => {
            setTimeout(() => {
              enableScroll();
              firstAnimationDone.current = true;
            }, 500);
          })
        );
      }
      else if (
        firstAnimationDone.current &&
        !secondAnimationDone.current &&
        y > 20
      ) {
        secondAnimationDone.current = true;
        setSecondAnimationStarted(true);
        disableScroll();
        requestAnimationFrame(() =>
          animate(performance.now(), stopFrame + 1, frameCount, () => {
            setTimeout(() => {
              enableScroll();
              setShowTable(true);
            }, 500);
          })
        );
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <section className="uzip">
      <div className="uzip-container">
        {/* sticky canvas + text */}
        <div className="uzip-sticky">
          <div className="uzip-animation">
            <div className={`uzip-bg ${textAnimated ? "slide-up-bg" : ""}`} />
            <canvas className="uzip-canvas" ref={canvasRef} />
            <div className="uzip-text">
              <div
                className={`uzip-description ${textAnimated ? "slide-up" : ""}`}
              >
                <span className="different-text">У</span>стройство
                <br />
                <span className="different-text">З</span>ащиты от
                <br />
                <span className="different-text">И</span>мпульсных
                <br />
                <span className="different-text">П</span>еренапряжений
              </div>
              <div
                className={`uzip__second-description ${textAnimated ? "fade-in-up" : ""
                  }`}
              >
                <h2 className="description-heading">
                  Для всех сигнальных цепей
                </h2>
                <span>
                  <p>
                    Напряжение <span className="different-style">5В </span>и{" "}
                    <span className="different-style">24В </span>
                  </p>
                  <p>Решения для 2, 3, 4 линий</p>
                  <p>
                    <span className="different-style">Модульный </span>
                    дизайн
                  </p>
                  <div className="explosion-proof-wrapper">
                    <div className="line-top">
                      <span className="another-style">Искробезопасное</span>
                      <span
                        className={`new-text ${secondAnimationStarted ? "fade-in" : ""
                          }`}
                      >
                        и Общепромышленное
                      </span>
                    </div>
                    <div className="line-bottom">исполнение</div>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* скроллируемый "пустой" контент для имитации прокрутки */}
        <div className="scroll-spacer" />
      </div>
      <table className={`uzip-table ${showTable ? "show" : ""}`}>
        <thead>
          <tr>
            <th rowSpan={2} className="another-bg"></th>
            <th colSpan={2}>Искробезопасные</th>
            <th colSpan={2}>Общепромышленные</th>
          </tr>
          <tr>
            <th>5 В</th>
            <th>24 В</th>
            <th>5 В</th>
            <th>24 В</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>2 линии</td>
            <td>PHL-T5-L2-EX</td>
            <td>PHL-T24-L2-EX</td>
            <td>PHL-T5-L2</td>
            <td>PHL-T24-L2</td>
          </tr>
          <tr>
            <td>3 линии</td>
            <td>PHL-T5-L3-EX</td>
            <td>PHL-T24-L3-EX</td>
            <td>PHL-T5-L3</td>
            <td>PHL-T24-L3</td>
          </tr>
          <tr>
            <td>4 линии</td>
            <td>PHL-T5-L4-EX</td>
            <td>PHL-T24-L4-EX</td>
            <td>PHL-T5-L4</td>
            <td>PHL-T24-L4</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

export default UZIP;
